'use client';

import { useEffect, useState } from 'react';

import { ChevronRight, FlaskConical } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Button } from '@naumu/ui/components/button';
import { Tooltip } from '@naumu/ui/components/tooltip';
import {
	isDocNew,
	slugifyGroup,
	type DocMeta,
	type DocsNavGroup,
} from '@/modules/docs/helpers/docs-loader';
import { cn } from '@naumu/ui/utils/cn';

/** Row styling shared by top-level articles and their nested subpages. */
const itemClass = (isActive: boolean): string =>
	cn(
		'flex items-center gap-2 rounded-[10px] px-3 py-[7px] text-[13px] transition-colors',
		isActive
			? 'bg-neutral/8 font-semibold text-neutral'
			: 'font-medium text-neutral/60 hover:bg-neutral/5'
	);

/**
 * Row title with its markers: the truncating label, a flask hugging the text
 * for beta/experimental docs, and a right-aligned week-long "New" pill driven
 * by the `new:` frontmatter date. `nowMs` is null until mount — the pill is
 * client-only so it expires at view time without a hydration mismatch, fading
 * in via the shared enter utilities.
 */
const RowLabel = ({
	meta,
	nowMs,
}: {
	meta: DocMeta;
	nowMs: number | null;
}): React.JSX.Element => (
	<>
		<span className="flex min-w-0 flex-1 items-center gap-1.5">
			<span className="min-w-0 truncate">{meta.title}</span>
			{meta.status && (
				<Tooltip
					content={
						meta.status === 'beta'
							? 'This is a beta feature'
							: 'This is an experimental feature'
					}
					position="top"
					triggerClassName="shrink-0"
				>
					<FlaskConical aria-hidden className="size-3 text-neutral/40" strokeWidth={1.5} />
					<span className="sr-only">({meta.status})</span>
				</Tooltip>
			)}
		</span>
		{nowMs !== null && isDocNew(meta, nowMs) && (
			<span className="shrink-0 animate-in fade-in rounded-full bg-accent/10 px-[7px] py-px text-[10px] font-semibold text-accent">
				New
			</span>
		)}
	</>
);

export const DocsSidebar = ({
	navGroups,
}: {
	navGroups: DocsNavGroup[];
}): React.JSX.Element => {
	const pathname = usePathname();

	// Chevron-toggled expansion, keyed by parent slug. It overrides the
	// URL-derived state so a branch can be peeked without navigating (which
	// inside the mobile drawer would also dismiss it); navigation clears the
	// overrides so the URL stays the source of truth.
	const [expandOverrides, setExpandOverrides] = useState<Record<string, boolean>>({});
	useEffect(() => {
		setExpandOverrides({});
	}, [pathname]);

	// Set once on mount; "New" pills render client-only against this timestamp.
	const [nowMs, setNowMs] = useState<number | null>(null);
	useEffect(() => {
		setNowMs(Date.now());
	}, []);

	return (
		<nav
			aria-label="Documentation"
			className="w-[264px] shrink-0 overflow-y-auto bg-background px-[14px] py-6"
		>
			{navGroups.map((group) => {
				const groupHref = `/docs/category/${slugifyGroup(group.group)}`;
				const isGroupActive = pathname === groupHref;
				return (
					<div key={group.group} className="mb-5">
						<Link
							href={groupHref}
							aria-current={isGroupActive ? 'page' : undefined}
							className={cn(
								'block px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide transition-colors',
								isGroupActive ? 'text-neutral' : 'text-neutral/40 hover:text-neutral/60'
							)}
						>
							{group.group}
						</Link>
						<div className="flex flex-col gap-px">
							{group.items.map((item) => {
								const href = `/docs/${item.slug}`;
								const isActive = pathname === href;
								if (item.children.length === 0) {
									return (
										<Link
											key={item.slug}
											href={href}
											aria-current={isActive ? 'page' : undefined}
											className={itemClass(isActive)}
										>
											<RowLabel meta={item} nowMs={nowMs} />
										</Link>
									);
								}

								// A deep link lands with the right branch open: expansion derives
								// from the URL (parent or subpage active) unless the chevron
								// toggled it this page-view.
								const derivedExpanded =
									isActive ||
									item.children.some((child) => pathname === `/docs/${child.slug}`);
								const isExpanded = expandOverrides[item.slug] ?? derivedExpanded;
								const subpagesId = `docs-nav-subpages-${item.slug}`;

								return (
									<div key={item.slug} className="flex flex-col gap-px">
										<div
											className={cn(
												'flex items-center rounded-[10px] pr-1 transition-colors',
												isActive ? 'bg-neutral/8' : 'hover:bg-neutral/5'
											)}
										>
											<Link
												href={href}
												aria-current={isActive ? 'page' : undefined}
												className={cn(
													'flex min-w-0 flex-1 items-center gap-2 px-3 py-[7px] text-[13px] transition-colors',
													isActive
														? 'font-semibold text-neutral'
														: 'font-medium text-neutral/60'
												)}
											>
												<RowLabel meta={item} nowMs={nowMs} />
											</Link>
											<Button
												variant="ghost"
												size="xs"
												iconOnly
												aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.title} subpages`}
												aria-expanded={isExpanded}
												aria-controls={subpagesId}
												onClick={(event) => {
													// Keep the tap from bubbling to the mobile drawer's
													// tap-anywhere-to-close body: the chevron peeks the
													// branch without navigating or dismissing anything.
													event.stopPropagation();
													setExpandOverrides((prev) => ({
														...prev,
														[item.slug]: !isExpanded,
													}));
												}}
												className="shrink-0"
											>
												<ChevronRight
													aria-hidden
													className={cn(
														// Back-out ease overshoots the quarter turn slightly
														// for a springy snap when the branch opens.
														'transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
														isExpanded && 'rotate-90'
													)}
													strokeWidth={2}
												/>
											</Button>
										</div>
										{/* Always mounted so collapse/expand can transition; the
										    0fr→1fr grid-row trick animates the auto height (same
										    mechanism as `removing-item-vertical`). `inert` keeps the
										    hidden links out of tab order and the accessibility tree. */}
										<div
											id={subpagesId}
											inert={!isExpanded}
											className={cn(
												'grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
												isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
											)}
										>
											<div className="overflow-hidden">
												<div className="ml-3 flex flex-col gap-px border-l border-neutral/10 pl-2 pt-px">
													{item.children.map((child, childIndex) => {
														const childHref = `/docs/${child.slug}`;
														const isChildActive = pathname === childHref;
														return (
															<Link
																key={child.slug}
																href={childHref}
																aria-current={isChildActive ? 'page' : undefined}
																className={cn(
																	itemClass(isChildActive),
																	'transition-[opacity,translate,background-color] duration-300',
																	isExpanded
																		? 'translate-x-0 opacity-100'
																		: '-translate-x-1 opacity-0'
																)}
																style={{
																	// Stagger each row in behind the height reveal;
																	// collapse fades everything out together.
																	transitionDelay: isExpanded
																		? `${80 + childIndex * 40}ms`
																		: '0ms',
																}}
															>
																<RowLabel meta={child} nowMs={nowMs} />
															</Link>
														);
													})}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
		</nav>
	);
};
